<script  module>
  import AppLayout from '$lib/layouts/AppLayout.svelte'
  export const layout = AppLayout
</script>

<script lang="ts">
  import { usePage, router } from "@inertiajs/svelte";
  import { 
    Zap, 
    Eye, 
    ExternalLink,
    Crown,
    Sparkles,
    Check,
    QrCode,
    ArrowUpRight,
    MessageSquareText,
    Users,
    TrendingUp,
    FileText,
    RefreshCw,
    History
  } from "lucide-svelte";
  import { toast } from "svelte-sonner";
  import * as Dialog from "$lib/components/ui/dialog/index.js";
  import { Button } from "$lib/components/ui/button/index.js";
  import { appStore } from '$lib/stores/appStore';
  import QRCode from '@castlenine/svelte-qrcode';

  import { 
    Chart, 
    Svg, 
    Axis, 
    Area, 
    Spline, 
    Bar, 
    Bars, 
    Tooltip, 
    Highlight,
    LinearGradient
  } from 'layerchart';
  import { scaleBand } from 'd3-scale';
  import * as Popover from "$lib/components/ui/popover/index.js";
  import TopPagesChart, { type TopPageItem } from '$lib/components/dashboard/TopPagesChart.svelte';
  import TrafficSourcesChart, { type TrafficSourceItem } from '$lib/components/dashboard/TrafficSourcesChart.svelte';
  import TopCountriesChart, { type CountryItem } from '$lib/components/dashboard/TopCountriesChart.svelte';

  interface DashboardProps {
    subscription?: {
      plan_name: string;
      plan_key: string;
      is_trial: boolean;
      days_left: number;
      total_trial_days?: number;
      expires_at: string;
      pv_limit: number;
      storage_limit_mb: number;
    };
    stats?: {
      today_pv: number;
      yesterday_pv: number;
      today_uv: number;
      yesterday_uv: number;
      pv_growth_rate: number;
      today_inquiries: number;
      yesterday_inquiries: number;
      total_inquiries: number;
      total_pages: number;
      media_usage_mb: number;
      page_load_ms: number;
      conversion_rate?: number;
      monthly_updates?: number;
    };
    plans?: Array<{
      id: string;
      name: string;
      price: string;
      period: string;
      description: string;
      features: string[];
      popular: boolean;
    }>;
    traffic_trends?: Array<{ hour: string; pv: number; uv: number }>;
    seo_trend?: { labels: string[]; spider_visits: number[] };
    top_pages?: TopPageItem[];
    traffic_sources?: TrafficSourceItem[];
    top_countries?: CountryItem[];
  }

  let { 
    subscription = {
      plan_name: '',
      plan_key: 'trial',
      is_trial: true,
      days_left: 7,
      total_trial_days: 14,
      expires_at: '',
      pv_limit: 10000,
      storage_limit_mb: 500
    },
    stats = {
      today_pv: 0,
      yesterday_pv: 0,
      today_uv: 0,
      yesterday_uv: 0,
      pv_growth_rate: 0,
      today_inquiries: 0,
      yesterday_inquiries: 0,
      total_inquiries: 0,
      total_pages: 0,
      media_usage_mb: 0,
      page_load_ms: 45,
      conversion_rate: 2.4,
      monthly_updates: 12,
    },
    plans = [],
    traffic_trends = [],
    seo_trend = { labels: [], spider_visits: [] },
    top_pages = [],
    traffic_sources = [],
    top_countries = [],
  }: DashboardProps = $props();

  const page = usePage();

  let currentSite = $derived(page.props?.current_site as any);
  let siteRegion = $derived(page.props.site_region);
  let appEnv = $derived(page.props.app_env);
  const translations = $derived((page.props as any).translations || {});
  const tl = $derived(translations.dashboard || {}); 

  // 辅助翻译函数：读取 Rails 共享的 I18n 数据（如 page.props.translations 或可通过 keyPath 路径寻找）
  function t(path: string, params: Record<string, any> = {}): string {
    const keys = path.split('.');
    let current: any = page.props?.translations || page.props?.t || {};
    
    for (const key of keys) {
      if (current && typeof current === 'object' && key in current) {
        current = current[key];
      } else {
        return path; // Fallback 返回 Key Path
      }
    }
    
    if (typeof current !== 'string') return path;
    
    // 替换模板变量，例如 %{days}
    return current.replace(/%\{(\w+)\}/g, (_, key) => {
      return params[key] !== undefined ? String(params[key]) : `%{${key}}`;
    });
  }

  // 弹窗状态
  let upgradeDialogOpen = $state(false);
  let selectedPlanId = $state('pro');

  // 站点健康状态
  let siteHealth = $state<'testing' | 'online' | 'offline'>('testing');

  $effect(() => {
    appStore.setTitle(t('dashboard.page_title'));
    checkSiteStatus();
  });

  async function checkSiteStatus() {
    if (!currentSite?.primary_domain) {
      siteHealth = 'offline';
      return;
    }
    
    siteHealth = 'testing';
    
    try {
      const res = await fetch('/site/check_health');
      const data = await res.json();
      
      siteHealth = data.health;
    } catch (e) {
      siteHealth = 'offline';
    }
  }

  // 格式化 SEO 蜘蛛数据
  let formattedSeoData = $derived(
    (seo_trend?.labels || []).map((label, index) => ({
      date: String(label || ''),
      visits: Number(seo_trend?.spider_visits?.[index] ?? 0)
    }))
  );
  
  console.log(JSON.stringify(formattedSeoData));

  function handleSelectPlan(planId: string) {
    selectedPlanId = planId;
  }

  function handleCheckout() {
    toast.loading(t('dashboard.toast.preparing_checkout'));
    router.post('/checkout', { plan_id: selectedPlanId }, {
      onSuccess: () => {
        upgradeDialogOpen = false;
      }
    });
  }
</script>

<svelte:head>
  <title>{t('dashboard.browser_title')}</title>
</svelte:head>

<div class="bg-background text-foreground w-full p-2 md:p-4 space-y-6">
  {#if appEnv != 'production'}
  <!-- ==================== 💳 1. 租户套餐 Banner ==================== -->
  <div class="relative overflow-hidden rounded-xl border bg-card p-4 md:p-5 shadow-sm">
    <div class="flex flex-col md:flex-row md:items-center justify-between gap-4 relative z-10">
      
      <div class="flex items-start md:items-center gap-3">
        <div class="p-2.5 rounded-xl bg-primary/10 text-primary shrink-0">
          {#if subscription.is_trial}
            <Sparkles class="size-6" />
          {:else}
            <Crown class="size-6" />
          {/if}
        </div>
        <div>
          <div class="flex items-center gap-2">
            <h2 class="text-base font-bold tracking-tight">
              {t('dashboard.banner.current_plan')} <span class="text-primary">{subscription.plan_name || t('dashboard.plans_data.trial_name')}</span>
            </h2>
            {#if subscription.is_trial}
              <span class="inline-flex items-center rounded-full bg-secondary px-2.5 py-0.5 text-xs font-semibold text-secondary-foreground border">
                {t('dashboard.banner.trial_remaining', { days: subscription.days_left })}
              </span>
            {:else}
              <span class="inline-flex items-center rounded-full bg-emerald-500/10 px-2.5 py-0.5 text-xs font-semibold text-emerald-600 dark:text-emerald-400 border border-emerald-500/20">
                {t('dashboard.banner.active_until', { expires_at: subscription.expires_at })}
              </span>
            {/if}
          </div>
          <p class="text-xs text-muted-foreground mt-1">
            {#if subscription.is_trial}
              {t('dashboard.banner.trial_tip')}
            {:else}
              {t('dashboard.banner.quota_tip', { 
                pv_limit: subscription.pv_limit.toLocaleString(), 
                storage_limit_mb: subscription.storage_limit_mb 
              })}
            {/if}
          </p>
        </div>
      </div>

      <div class="flex items-center gap-3 shrink-0">
        <Button 
          onclick={() => upgradeDialogOpen = true}
          class="font-medium shadow-sm"
        >
          <Crown class="size-4 mr-1.5" />
          {subscription.is_trial ? t('dashboard.banner.btn_upgrade') : t('dashboard.banner.btn_renew')}
        </Button>
      </div>

    </div>
  </div>
{/if}

  <!-- ==================== 🌐 站点名称 ==================== -->
  <div class="flex items-center justify-between border-b pb-4">
    <div class="flex items-center gap-2.5">
      <h1 class="text-xl font-bold tracking-tight">
        {#if currentSite?.primary_domain}
          <a 
            href="http://{currentSite.primary_domain}" 
            target="_blank" 
            rel="noopener noreferrer"
            class="hover:text-primary transition-colors inline-flex items-center gap-1.5 group"
          >
            {currentSite?.name || t('dashboard.default_site_name')}
            <ExternalLink class="size-4 text-muted-foreground group-hover:text-primary transition-colors" />
          </a>
          <Popover.Root>
            <Popover.Trigger class="inline-flex items-center justify-center size-5 text-muted-foreground hover:text-foreground hover:bg-muted rounded-md transition-colors cursor-pointer" title={tl.show_qrcode || '显示网站二维码'}>
              <QrCode class="size-3.5" />
            </Popover.Trigger>
            
            <Popover.Content class="w-48 p-3 rounded-xl border bg-popover text-popover-foreground shadow-md z-50">
              <div class="flex flex-col items-center gap-2">
                <div class="size-36 bg-white p-1 rounded-lg border border-border flex items-center justify-center">
                  <QRCode data="http://{currentSite.primary_domain}" isResponsive />
                </div>
                <span class="text-[10px] text-muted-foreground text-center truncate w-full">
                  { t.qrcode_title || 'Scan to Visit Site' }
                </span>
              </div>
            </Popover.Content>
          </Popover.Root>
        {:else}
          {currentSite?.name || t('dashboard.default_site_name')}
        {/if}
      </h1>

      <div class="flex items-center gap-1.5 ml-1">
        {#if siteHealth === 'online'}
          <span class="relative flex h-2.5 w-2.5" title={t('dashboard.status.online_title')}>
            <span class="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
            <span class="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-500"></span>
          </span>
          <span class="text-xs text-emerald-600 dark:text-emerald-400 font-medium">{t('dashboard.status.online')}</span>
        {:else if siteHealth === 'testing'}
          <RefreshCw class="size-3 animate-spin text-muted-foreground" />
          <span class="text-xs text-muted-foreground">{t('dashboard.status.testing')}</span>
        {:else}
          <span class="h-2.5 w-2.5 rounded-full bg-destructive" title={t('dashboard.status.offline_title')}></span>
          <span class="text-xs text-destructive font-medium">{t('dashboard.status.offline')}</span>
        {/if}
      </div>
    </div>

    <Button variant="ghost" size="sm" onclick={checkSiteStatus} class="text-xs text-muted-foreground">
      <RefreshCw class="size-3.5 mr-1" />
      {t('dashboard.status.recheck')}
    </Button>
  </div>

  <!-- ==================== 🛠️ 2. 核心业务 KPI 卡片 ==================== -->
  <div class="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
    
    <!-- 1. 今日浏览量 (PV) -->
    <div class="rounded-xl border bg-card p-4 shadow-sm flex flex-col justify-between">
      <div class="flex items-center justify-between">
        <p class="text-xs font-medium text-muted-foreground uppercase tracking-wider">{t('dashboard.kpi.today_pv')}</p>
        <Eye class="h-4 w-4 text-primary" />
      </div>
      <div class="mt-2">
        <span class="text-3xl font-extrabold tracking-tight">{stats.today_pv}</span>
        <span class="text-xs text-muted-foreground ml-1">{t('dashboard.kpi.pv_unit')}</span>
      </div>
      <div class="mt-3 flex items-center justify-between text-xs text-muted-foreground border-t pt-2">
        <span>{t('dashboard.kpi.pv_growth_vs_yesterday')}</span>
        <span class="{stats.pv_growth_rate >= 0 ? 'text-emerald-600' : 'text-rose-600'} font-semibold">
          {stats.pv_growth_rate >= 0 ? '+' : ''}{stats.pv_growth_rate}%
        </span>
      </div>
    </div>

    <!-- 2. 今日独立访客 (UV) -->
    <div class="rounded-xl border bg-card p-4 shadow-sm flex flex-col justify-between">
      <div class="flex items-center justify-between">
        <p class="text-xs font-medium text-muted-foreground uppercase tracking-wider">{t('dashboard.kpi.today_uv')}</p>
        <Users class="h-4 w-4 text-primary" />
      </div>
      <div class="mt-2">
        <span class="text-3xl font-extrabold tracking-tight">{stats.today_uv}</span>
        <span class="text-xs text-muted-foreground ml-1">{t('dashboard.kpi.uv_unit')}</span>
      </div>
      <div class="mt-3 flex items-center justify-between text-xs text-muted-foreground border-t pt-2">
        <span>{t('dashboard.kpi.realtime_stat')}</span>
        <span class="text-emerald-600 font-medium">{t('dashboard.kpi.realtime')}</span>
      </div>
    </div>

    <!-- 3. 昨日流量概况 (PV / UV) -->
    <div class="rounded-xl border bg-card p-4 shadow-sm flex flex-col justify-between bg-muted/20">
      <div class="flex items-center justify-between">
        <p class="text-xs font-medium text-muted-foreground uppercase tracking-wider">{t('dashboard.kpi.yesterday_traffic')}</p>
        <History class="h-4 w-4 text-muted-foreground" />
      </div>
      <div class="mt-2 flex items-baseline gap-3">
        <div>
          <span class="text-2xl font-bold tracking-tight">{stats.yesterday_pv}</span>
          <span class="text-xs text-muted-foreground ml-0.5">{t('dashboard.kpi.pv_label')}</span>
        </div>
        <div class="h-4 w-px bg-border"></div>
        <div>
          <span class="text-2xl font-bold tracking-tight">{stats.yesterday_uv}</span>
          <span class="text-xs text-muted-foreground ml-0.5">{t('dashboard.kpi.uv_label')}</span>
        </div>
      </div>
      <div class="mt-3 flex items-center justify-between text-xs text-muted-foreground border-t pt-2">
        <span>{t('dashboard.kpi.full_day_settled')}</span>
        <span>{t('dashboard.kpi.archived')}</span>
      </div>
    </div>

    <!-- 4. 今日线索 / 询盘 -->
    <div class="rounded-xl border bg-card p-4 shadow-sm flex flex-col justify-between">
      <div class="flex items-center justify-between">
        <p class="text-xs font-medium text-muted-foreground uppercase tracking-wider">{t('dashboard.kpi.today_inquiries')}</p>
        <MessageSquareText class="h-4 w-4 text-primary" />
      </div>
      <div class="mt-2 flex items-baseline gap-2">
        <span class="text-3xl font-extrabold tracking-tight">{stats.today_inquiries}</span>
        <span class="text-xs text-muted-foreground">{t('dashboard.kpi.inquiry_unit')}</span>
      </div>
      <div class="mt-3 flex items-center justify-between text-xs text-muted-foreground border-t pt-2">
        <span>{t('dashboard.kpi.total_captured')} <strong class="text-foreground">{stats.total_inquiries}</strong></span>
        <span class="text-primary font-medium">{t('dashboard.kpi.instant_receive')}</span>
      </div>
    </div>

    <!-- 5. 昨日询盘 -->
    <div class="rounded-xl border bg-card p-4 shadow-sm flex flex-col justify-between bg-muted/20">
      <div class="flex items-center justify-between">
        <p class="text-xs font-medium text-muted-foreground uppercase tracking-wider">{t('dashboard.kpi.yesterday_inquiries')}</p>
        <History class="h-4 w-4 text-muted-foreground" />
      </div>
      <div class="mt-2 flex items-baseline gap-2">
        <span class="text-3xl font-extrabold tracking-tight">{stats.yesterday_inquiries}</span>
        <span class="text-xs text-muted-foreground">{t('dashboard.kpi.inquiry_unit')}</span>
      </div>
      <div class="mt-3 flex items-center justify-between text-xs text-muted-foreground border-t pt-2">
        <span>{t('dashboard.kpi.yesterday_archived_inquiries')}</span>
        <span>{t('dashboard.kpi.in_database')}</span>
      </div>
    </div>

    <!-- 6. 询盘转化率 -->
    <div class="rounded-xl border bg-card p-4 shadow-sm flex flex-col justify-between">
      <div class="flex items-center justify-between">
        <p class="text-xs font-medium text-muted-foreground uppercase tracking-wider">{t('dashboard.kpi.conversion_rate')}</p>
        <TrendingUp class="h-4 w-4 text-primary" />
      </div>
      <div class="mt-2 flex items-baseline gap-1">
        <span class="text-3xl font-extrabold tracking-tight">{stats.conversion_rate ?? 0}</span>
        <span class="text-xs font-semibold">%</span>
      </div>
      <div class="mt-3 flex items-center justify-between text-xs text-muted-foreground border-t pt-2">
        <span>{t('dashboard.kpi.conversion_rate_tip')}</span>
        <span class="text-emerald-600 font-medium">{t('dashboard.kpi.performance_good')}</span>
      </div>
    </div>

    <!-- 7. 近 30 天内容更新 -->
    <div class="rounded-xl border bg-card p-4 shadow-sm flex flex-col justify-between">
      <div class="flex items-center justify-between">
        <p class="text-xs font-medium text-muted-foreground uppercase tracking-wider">{t('dashboard.kpi.recent_updates')}</p>
        <FileText class="h-4 w-4 text-primary" />
      </div>
      <div class="mt-2 flex items-baseline gap-1">
        <span class="text-3xl font-extrabold tracking-tight">{stats.monthly_updates ?? 0}</span>
        <span class="text-xs text-muted-foreground">{t('dashboard.kpi.pages_unit')}</span>
      </div>
      <div class="mt-3 flex items-center justify-between text-xs text-muted-foreground border-t pt-2">
        <span>{t('dashboard.kpi.total_published')} <strong>{stats.total_pages}</strong></span>
        <span>{t('dashboard.kpi.active_operation')}</span>
      </div>
    </div>

    <!-- 8. 站点资源 & 节点性能 -->
    <div class="rounded-xl border bg-card p-4 shadow-sm flex flex-col justify-between">
      <div class="flex items-center justify-between">
        <p class="text-xs font-medium text-muted-foreground uppercase tracking-wider">{t('dashboard.kpi.resources_and_node')}</p>
        <Zap class="h-4 w-4 text-primary" />
      </div>
      <div class="mt-2 flex items-baseline gap-3">
        <div>
          <span class="text-xl font-bold tracking-tight">{stats.media_usage_mb} MB</span>
          <span class="text-[10px] text-muted-foreground block">/ {subscription.storage_limit_mb}MB {t('dashboard.kpi.storage_label')}</span>
        </div>
        <div class="h-4 w-px bg-border"></div>
        <div>
          <span class="text-xl font-bold tracking-tight">{stats.page_load_ms}ms</span>
          <span class="text-[10px] text-muted-foreground block">{t('dashboard.kpi.avg_latency')}</span>
        </div>
      </div>
      <div class="mt-3 flex items-center justify-between text-xs text-muted-foreground border-t pt-2">
        <span>{t('dashboard.kpi.cdn_status')}</span>
        <span class="text-emerald-600 font-medium">{t('dashboard.kpi.performance_excellent')}</span>
      </div>
    </div>

  </div>

  <!-- ==================== 📈 3. 图表区 ==================== -->
  <div class="grid gap-6 md:grid-cols-2">
    
    <!-- 图表 1：24小时 PV / UV 趋势图 -->
    <div class="rounded-xl border bg-card p-5 shadow-sm flex flex-col">
      <div class="flex items-center justify-between mb-4">
        <div>
          <h3 class="text-sm font-semibold">{t('dashboard.charts.traffic_trend_title')}</h3>
          <p class="text-xs text-muted-foreground">{t('dashboard.charts.traffic_trend_desc')}</p>
        </div>
        <div class="flex items-center gap-3 text-xs">
          <span class="flex items-center gap-1"><span class="size-2 rounded-full bg-primary"></span> {t('dashboard.kpi.pv_label')}</span>
          <span class="flex items-center gap-1"><span class="size-2 rounded-full bg-muted-foreground"></span> {t('dashboard.kpi.uv_label')}</span>
        </div>
      </div>
      
      <div class="h-56 w-full relative">
        {#if traffic_trends && traffic_trends.length > 0}
          <Chart
            data={traffic_trends}
            x="hour"
            xScale={scaleBand().padding(0.2)}
            y={['pv', 'uv']}
            yDomain={[0, null]}
            padding={{ top: 10, bottom: 24, left: 28, right: 10 }}
            tooltip={{ mode: 'band' }}
          >
            <Svg>
              <Axis placement="bottom" grid class="text-[10px] text-muted-foreground" />
              <Axis placement="left" grid class="text-[10px] text-muted-foreground" />

              <!-- 先画 UV，再画 PV，形成重叠效果 -->
              <Bars y="uv" class="fill-muted-foreground/40" />
              <Bars y="pv" class="fill-primary/80" />

              <Highlight bar class="fill-primary/10" />
            </Svg>

            <Tooltip.Root>
              {#snippet children({ data })}
                {#if data}
                  <Tooltip.Header>{data.hour ?? ''} {t('dashboard.charts.detail_suffix')}</Tooltip.Header>
                  <Tooltip.List>
                    <Tooltip.Item label={t('dashboard.charts.pv_item')} value={data.pv ?? 0} class="text-primary" />
                    <Tooltip.Item label={t('dashboard.charts.uv_item')} value={data.uv ?? 0} class="text-muted-foreground" />
                  </Tooltip.List>
                {/if}
              {/snippet}
            </Tooltip.Root>
          </Chart>
        {:else}
          <div class="flex h-full items-center justify-center text-xs text-muted-foreground">
            {t('dashboard.charts.no_traffic_data')}
          </div>
        {/if}
      </div>
    </div>

    <!-- 图表 2：搜索引擎 Spider 抓取频次 -->
    <div class="rounded-xl border bg-card p-5 shadow-sm flex flex-col">
      <div class="mb-4">
        <h3 class="text-sm font-semibold">{t('dashboard.charts.spider_trend_title')}</h3>
        <p class="text-xs text-muted-foreground">{t('dashboard.charts.spider_trend_desc')}</p>
      </div>
      
      <div class="h-56 w-full relative">
        {#if formattedSeoData && formattedSeoData.length > 0}
          <Chart
            data={formattedSeoData}
            x="date"
            y="visits"
            xScale={scaleBand().padding(0.2)}
            yDomain={[0, Math.max(10, ...formattedSeoData.map(d => d.visits ?? 0))]}
            padding={{ top: 10, bottom: 24, left: 28, right: 10 }}
            tooltip={{ mode: 'band' }}
          >
            <Svg>
              <Axis placement="bottom" grid class="text-[10px] text-muted-foreground" />
              <Axis placement="left" grid class="text-[10px] text-muted-foreground" />

              <Bars class="fill-primary/80 hover:fill-primary rx-1 transition-all" />

              <Highlight bar class="fill-primary/10" />
            </Svg>

            <Tooltip.Root>
              {#snippet children({ data })}
                {#if data}
                  <Tooltip.Header>{data.date ?? ''}</Tooltip.Header>
                  <Tooltip.List>
                    <Tooltip.Item label={t('dashboard.charts.spider_visits')} value={data.visits ?? 0} class="text-primary" />
                  </Tooltip.List>
                {/if}
              {/snippet}
            </Tooltip.Root>
          </Chart>
        {:else}
          <div class="flex h-full items-center justify-center text-xs text-muted-foreground">
            {t('dashboard.charts.no_spider_data')}
          </div>
        {/if}
      </div>
    </div>


    <!-- 图表 3：Top 热门页面组件 -->
    <TopPagesChart {top_pages} {t} />

    <!-- 图表 4：流量来源渠道组件 -->
    <TrafficSourcesChart sources={traffic_sources} {t} />

    <!-- 图表 5：国家/地区分布组件 -->
  <TopCountriesChart countries={top_countries} {t} />
  </div>

  <!-- ==================== 💳 4. 套餐升级 Dialog 弹窗 ==================== -->
  <Dialog.Root bind:open={upgradeDialogOpen}>
    <Dialog.Content class="sm:max-w-[850px] p-6">
      <Dialog.Header>
        <Dialog.Title class="text-xl font-bold text-center flex items-center justify-center gap-2">
          <Crown class="size-5 text-primary" />
          {t('dashboard.dialog.title')}
        </Dialog.Title>
        <Dialog.Description class="text-center text-xs text-muted-foreground mt-1">
          {t('dashboard.dialog.description')}
        </Dialog.Description>
      </Dialog.Header>

      <div class="grid grid-cols-1 md:grid-cols-3 gap-4 my-6">
        {#each plans as plan}
          <div 
            role="button"
            tabindex="0"
            onclick={() => handleSelectPlan(plan.id)}
            onkeydown={(e) => (e.key === 'Enter' || e.key === ' ') && handleSelectPlan(plan.id)}
            class="relative rounded-xl border p-4 cursor-pointer transition-all flex flex-col justify-between {selectedPlanId === plan.id ? 'border-primary ring-2 ring-primary/20 bg-primary/5' : 'hover:border-border/80'}"
          >
            {#if plan.popular}
              <span class="absolute -top-3 right-4 bg-primary text-primary-foreground text-[10px] font-bold px-2 py-0.5 rounded-full shadow-sm">
                {t('dashboard.dialog.recommended')}
              </span>
            {/if}

            <div>
              <h4 class="font-bold text-base">{plan.name}</h4>
              <p class="text-xs text-muted-foreground mt-1 min-h-[32px]">{plan.description}</p>
              
              <div class="my-4">
                <span class="text-3xl font-extrabold">{plan.price}</span>
                <span class="text-xs text-muted-foreground">{plan.period}</span>
              </div>

              <ul class="space-y-2 border-t pt-3 text-xs text-muted-foreground">
                {#each plan.features as feat}
                  <li class="flex items-start gap-1.5">
                    <Check class="size-3.5 text-primary shrink-0 mt-0.5" />
                    <span>{feat}</span>
                  </li>
                {/each}
              </ul>
            </div>

            <Button 
              variant={selectedPlanId === plan.id ? "default" : "outline"} 
              class="w-full mt-6 text-xs"
            >
              {selectedPlanId === plan.id ? t('dashboard.dialog.selected') : t('dashboard.dialog.select')}
            </Button>
          </div>
        {/each}
      </div>

      <Dialog.Footer class="flex sm:justify-between items-center border-t pt-4">
        <span class="text-xs text-muted-foreground">{t('dashboard.dialog.payment_methods')}</span>
        <Button class="font-medium" onclick={handleCheckout}>
          {t('dashboard.dialog.next_checkout')}
          <ArrowUpRight class="size-4 ml-1" />
        </Button>
      </Dialog.Footer>
    </Dialog.Content>
  </Dialog.Root>

</div>